import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

import { OrderGoalTierChangeEvent } from '@dkl/events';
import { DklWidgetBase } from '@dkl/global';

/**
 * Discount Kit - Order Goal (Cart) widget
 *
 * Emits tier-change events based on the cart subtotal (items_subtotal_price)
 * relative to configured order goal tiers.
 */
class DklOrderGoal extends DklWidgetBase {
  /** @type {Array<{tierLevel: number, minSpendCents: number, discountAmount: number, discountType: string, message: string}>} */
  #tiers = [];

  /** @type {{tierLevel: number, minSpendCents: number, discountAmount: number, discountType: string, message: string} | null} */
  #lastTierMet = null;

  /** @type {boolean} */
  #isInitialLoad = true;

  #abortController = new AbortController();

  connectedCallback() {
    this.#parseTiers();

    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const initialTotal = this.#getInitialCartTotal();
    this.#updateForCartTotal(initialTotal);

    // Dispatch mount with current tier info
    const widgetType = this.dataset.dklWidgetType || 'order-goal';
    this.dispatchWidgetMount(widgetType, {
      currentTier: this.#lastTierMet,
      cartTotalCents: initialTotal,
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    const widgetType = this.dataset.dklWidgetType || 'order-goal';
    this.dispatchWidgetUnmount(widgetType);
  }

  /**
   * Parse tier data from child elements rendered by the Liquid snippet.
   */
  #parseTiers() {
    const tierEls = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.dkl-order-goal__tier')
    );

    this.#tiers = Array.from(tierEls).map((el, index) => {
      const minSpendCents = Number.parseInt(el.dataset.tierMinSpend || '0', 10) || 0;
      const discountType = el.dataset.tierDiscountType || '';
      const discountAmount = Number.parseFloat(el.dataset.tierDiscountAmount || '0') || 0;
      const message = el.dataset.tierMessage || '';

      return {
        tierLevel: index + 1, // 1-indexed for readability in events, rather than 0-indexed
        minSpendCents,
        discountAmount,
        discountType,
        message,
      };
    });

    // Ensure tierdevModes are sorted by minimum spend ascending
    this.#tiers.sort((a, b) => a.minSpendCents - b.minSpendCents);
  }

  /**
   * Returns the initial cart subtotal in cents from the data attribute.
   *
   * @returns {number}
   */
  #getInitialCartTotal() {
    const raw = this.dataset.dklCartTotalCents;
    const value = Number.parseInt(raw || '0', 10);
    return Number.isFinite(value) ? value : 0;
  }

  /**
   * Handle cart update events from the Horizon theme.
   *
   * Prefers the cart object from the event payload as the source of truth, if it exists (there are some edge 
   * cases where Shopify doesn't include the cart object in the event payload if requests are stale or something else has gone wrong).
   * If the payload does not include a usable subtotal, falls back to
   * fetching `/cart.js` to compute the current subtotal.
   *
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    /** @type {any} */
    const e = event;
    const detail = e?.detail;
    let cart = detail?.resource;
    const source = detail?.data?.source;

    // Check if this widget is on a PDP and needs to be updated via section rendering
    const widgetType = this.dataset.dklWidgetType;
    
    if (widgetType === 'order-goal-pdp') {
      // Shimmer on cart add (from product form) or cart update
      if (source === 'product-form-component' || detail?.data?.sections) {
        this.#shimmerWidget();
      }
      
      // Update widget content if sections are available
      if (detail?.data?.sections) {
        this.#updatePDPWidget(detail?.data?.sections);
      }
    }

    /**
     * @param {any} cartObj
     */
    const resolveAndUpdate = (cartObj) => {
      const subtotal =
        cartObj?.items_subtotal_price ??
        cartObj?.itemsSubtotalPrice ??
        null;

      if (!Number.isFinite(subtotal)) return;
      this.#updateForCartTotal(Number(subtotal));
    };

    // If the event already has a cart payload with a a subtotal, use it directly.
    if (cart && Number.isFinite(cart.items_subtotal_price)) {
      resolveAndUpdate(cart);
      return;
    }

    // Fallback: fetch the cart state from `/cart.js`.
    fetch('/cart.js')
      .then((response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .then((fetchedCart) => {
        if (!fetchedCart) return;
        resolveAndUpdate(fetchedCart);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to resolve cart subtotal for order goal widget:', error);
      });
  };

  /**
   * Apply shimmer effect to text components within the widget
   */
  #shimmerWidget() {
    const textComponents = /** @type {NodeListOf<HTMLElement & {shimmer: () => void}>} */ (
      this.querySelectorAll('text-component')
    );
    textComponents.forEach((component) => component.shimmer());
  }

  /**
   * Update the PDP widget by morphing it with updated section HTML from cart event.
   * @param {Record<string, string>} [sections] - The sections from the cart update event
   */
  #updatePDPWidget(sections) {
    if (!sections) {
      return;
    }

    // Find the product-information section in the sections response
    const productSectionId = this.closest('.shopify-section')?.id;
    if (!productSectionId) {
      return;
    }

    // Normalize section ID (remove 'shopify-section-' prefix if present)
    const normalizedSectionId = productSectionId.replace('shopify-section-', '');

    const sectionHTML = sections[productSectionId] || sections[normalizedSectionId];
    if (!sectionHTML) {
      return;
    }

    // Parse the new HTML
    const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newOrderGoal = fragment.querySelector('dkl-order-goal[data-dkl-widget-type="order-goal-pdp"]');
        
    if (newOrderGoal) {
      // Morph only the children to preserve the custom element
      morph(this, newOrderGoal, { childrenOnly: true });
      
      // Re-parse tiers after morphing
      this.#parseTiers();
      
      // Get the new cart total from the morphed element
      const newCartTotal = this.#getInitialCartTotal();
      this.#updateForCartTotal(newCartTotal);
    }
  }

  /**
   * Compute current tier from subtotal and emit tier-change events.
   *
   * @param {number} subtotalCents
   */
  #updateForCartTotal(subtotalCents) {
    /** @type {{ tierLevel: number; minSpendCents: number; discountAmount: number; discountType: string; message: string } | null} */
    let currentTier = null;
    for (const tier of this.#tiers) {
      if (subtotalCents >= tier.minSpendCents) {
        currentTier = tier;
      } else {
        break;
      }
    }

    const previousTier = this.#lastTierMet;

    const previousLevel = previousTier ? previousTier.tierLevel : 0;
    const currentLevel = currentTier ? currentTier.tierLevel : 0;

    // Skip if tier hasn't changed, or if this is the initial load
    if (previousLevel === currentLevel || this.#isInitialLoad) {
      this.#lastTierMet = currentTier;
      this.#isInitialLoad = false;
      return;
    }

    this.#lastTierMet = currentTier;

    const resource = {
      widgetId: this.widgetId,
      widgetType: this.dataset.dklWidgetType || 'order-goal',
      previousTier,
      currentTier,
      cartTotalCents: subtotalCents,
    };

    this.log('order-goal:tier-change', resource);
    this.dispatchEvent(new OrderGoalTierChangeEvent(resource));
  }
}


if (typeof customElements !== 'undefined' && !customElements.get('dkl-order-goal')) {
  customElements.define('dkl-order-goal', DklOrderGoal);
}





